import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Checkbox,
    Grid,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getToken } from '../../services/authService';
import { apiClient } from '../../api/apiClient';

const RoleList = () => {
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedRole, setSelectedRole] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editData, setEditData] = useState({
        name: '',
        description: '',
        permission_ids: []
    });

    // Zustand für die Dual-List-Box
    const [availablePermissions, setAvailablePermissions] = useState([]);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [checkedAvailable, setCheckedAvailable] = useState([]);
    const [checkedSelected, setCheckedSelected] = useState([]);

    const fetchRoles = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiClient.get(
                '/roles',
                {
                    headers: {
                        'Authorization': `Bearer ${getToken()}`
                    }
                }
            );
            setRoles(response.data);
        } catch (error) {
            console.error('Fehler beim Laden der Rollen:', error);
            setError(error.response?.data?.detail || 'Fehler beim Laden der Rollen');
        } finally {
            setLoading(false);
        }
    };

    const fetchPermissions = async () => {
        try {
            const response = await apiClient.get(
                '/permissions',
                {
                    headers: {
                        'Authorization': `Bearer ${getToken()}`
                    }
                }
            );
            console.log('Raw permissions from API:', response.data);
            
            // Stelle sicher, dass jede Permission eine ID hat
            const permissionsWithIds = response.data.map(p => {
                console.log('Processing permission:', p);
                return {
                    id: p.id || p.name, // Fallback auf name wenn keine ID vorhanden
                    name: p.name,
                    description: p.description
                };
            });
            console.log('Processed permissions:', permissionsWithIds);
            setPermissions(permissionsWithIds);
        } catch (error) {
            console.error('Fehler beim Laden der Berechtigungen:', error);
            setError(error.response?.data?.detail || 'Fehler beim Laden der Berechtigungen');
        }
    };

    useEffect(() => {
        fetchRoles();
        fetchPermissions();
    }, []);

    const handleCreateClick = () => {
        setEditData({
            name: '',
            description: '',
            permission_ids: []
        });
        
        // Stelle sicher, dass alle Properties der Permissions kopiert werden
        const availablePerms = permissions.map(p => ({
            id: p.id,
            name: p.name,
            description: p.description
        }));
        
        setAvailablePermissions(availablePerms);
        setSelectedPermissions([]);
        setCheckedAvailable([]);
        setCheckedSelected([]);
        setCreateDialogOpen(true);
    };

    const handleEditClick = (role) => {
        setSelectedRole(role);
        setEditData({
            name: role.name,
            description: role.description || '',
            permission_ids: role.permissions ? role.permissions.map(p => p.id) : []
        });
        
        // Setze die Berechtigungen für die Dual-List-Box
        const rolePermissions = role.permissions || [];
        
        // Filtere die verfügbaren Berechtigungen - nur die, die noch nicht zugewiesen sind
        const availablePerms = permissions.filter(p => 
            !rolePermissions.some(rp => rp.name === p.name)
        );
        
        setAvailablePermissions(availablePerms);
        setSelectedPermissions(rolePermissions);
        setCheckedAvailable([]);
        setCheckedSelected([]);
        
        setEditDialogOpen(true);
    };

    const handleDeleteClick = (role) => {
        setSelectedRole(role);
        setDeleteDialogOpen(true);
    };

    const handleCreateSave = async () => {
        try {
            console.log('Current selectedPermissions:', selectedPermissions);
            console.log('Current editData:', editData);
            
            // Stelle sicher, dass selectedPermissions ein Array ist und gültige IDs enthält
            const validPermissionIds = (selectedPermissions || [])
                .filter(p => p && typeof p === 'object')
                .map(p => p.id || p.name)  // Verwende name als Fallback
                .filter(id => id != null);  // Filtere undefined/null
            
            console.log('Valid Permission IDs:', validPermissionIds);

            const data = {
                name: editData.name,
                description: editData.description,
                permission_ids: validPermissionIds
            };
            
            console.log('Final Request Data:', data);
            
            await apiClient.post('/roles', data, {
                headers: {
                    'Authorization': `Bearer ${getToken()}`
                }
            });
            
            setCreateDialogOpen(false);
            fetchRoles();
            setError(null);
        } catch (error) {
            console.error('Fehler beim Erstellen der Rolle:', error);
            const errorMessage = error.response?.data?.detail || 
                               error.message || 
                               'Fehler beim Erstellen der Rolle';
            setError(errorMessage);
        }
    };

    const handleEditSave = async () => {
        if (!selectedRole) return;

        try {
            const data = {
                name: editData.name,
                description: editData.description,
                permission_ids: selectedPermissions.map(p => parseInt(p.id))
            };
            
            await apiClient.put(
                `/roles/${selectedRole.id}`,
                data,
                {
                    headers: {
                        'Authorization': `Bearer ${getToken()}`
                    }
                }
            );
            setEditDialogOpen(false);
            setSelectedRole(null);
            fetchRoles();
            setError(null);
        } catch (error) {
            console.error('Fehler beim Aktualisieren der Rolle:', error);
            const errorMessage = error.response?.data?.detail || 
                               error.message || 
                               'Fehler beim Aktualisieren der Rolle';
            setError(errorMessage);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!selectedRole) return;

        try {
            await apiClient.delete(
                `/roles/${selectedRole.id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${getToken()}`
                    }
                }
            );
            setDeleteDialogOpen(false);
            setSelectedRole(null);
            fetchRoles();
        } catch (error) {
            console.error('Fehler beim Löschen der Rolle:', error);
            setError(error.response?.data?.detail || 'Fehler beim Löschen der Rolle');
        }
    };

    const handleToggleAvailable = (permission) => {
        const currentIndex = checkedAvailable.indexOf(permission);
        const newChecked = [...checkedAvailable];

        if (currentIndex === -1) {
            newChecked.push(permission);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setCheckedAvailable(newChecked);
    };

    const handleToggleSelected = (permission) => {
        const currentIndex = checkedSelected.indexOf(permission);
        const newChecked = [...checkedSelected];

        if (currentIndex === -1) {
            newChecked.push(permission);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setCheckedSelected(newChecked);
    };

    const handleMoveToSelected = () => {
        console.log('Checked Available before move:', checkedAvailable);
        
        // Füge ausgewählte Berechtigungen hinzu
        const newSelected = [...selectedPermissions];
        checkedAvailable.forEach(permission => {
            console.log('Moving permission with full details:', permission);
            if (!newSelected.some(p => p.id === permission.id)) {
                const permissionToAdd = {
                    id: permission.id || permission.name, // Fallback auf name wenn keine ID vorhanden
                    name: permission.name,
                    description: permission.description
                };
                console.log('Adding permission:', permissionToAdd);
                newSelected.push(permissionToAdd);
            }
        });
        console.log('New Selected Permissions:', newSelected);
        setSelectedPermissions(newSelected);

        // Entferne sie aus den verfügbaren Berechtigungen
        const newAvailable = availablePermissions.filter(
            p => !checkedAvailable.some(cp => cp.id === p.id || cp.name === p.name)  // Vergleiche auch Namen
        );
        console.log('New Available Permissions:', newAvailable);
        setAvailablePermissions(newAvailable);
        setCheckedAvailable([]);
    };

    const handleMoveToAvailable = () => {
        // Füge ausgewählte Berechtigungen zurück zu verfügbaren hinzu
        const newAvailable = [...availablePermissions];
        checkedSelected.forEach(permission => {
            if (!newAvailable.some(p => p.id === permission.id)) {
                newAvailable.push(permission);
            }
        });
        setAvailablePermissions(newAvailable);

        // Entferne sie aus den ausgewählten Berechtigungen
        setSelectedPermissions(selectedPermissions.filter(
            p => !checkedSelected.some(cp => cp.id === p.id)
        ));
        setCheckedSelected([]);
    };

    const getErrorMessage = (error) => {
        if (!error) return '';
        if (typeof error === 'string') return error;
        if (error.response?.data?.detail) return error.response.data.detail;
        if (error.message) return error.message;
        return 'Ein unbekannter Fehler ist aufgetreten';
    };

    if (loading) {
        return (
            <Box p={3}>
                <Typography>Lade Daten...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box p={3}>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {getErrorMessage(error)}
                </Alert>
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5">Rollenverwaltung</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleCreateClick}
                >
                    Neue Rolle
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Beschreibung</TableCell>
                            <TableCell>Aktionen</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {roles.map((role) => (
                            <TableRow key={role.id || role.name}>
                                <TableCell>{role.name}</TableCell>
                                <TableCell>{role.description}</TableCell>
                                <TableCell>
                                    <Tooltip title="Bearbeiten">
                                        <IconButton onClick={() => handleEditClick(role)}>
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Löschen">
                                        <IconButton onClick={() => handleDeleteClick(role)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Dialog für Erstellen/Bearbeiten */}
            <Dialog
                open={editDialogOpen || createDialogOpen}
                onClose={() => {
                    setEditDialogOpen(false);
                    setCreateDialogOpen(false);
                    setSelectedRole(null);
                    setError(null);
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {createDialogOpen ? 'Neue Rolle erstellen' : 'Rolle bearbeiten'}
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {getErrorMessage(error)}
                        </Alert>
                    )}
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="Name"
                            value={editData.name}
                            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Beschreibung"
                            value={editData.description}
                            onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                            sx={{ mb: 3 }}
                        />
                        
                        <Typography variant="subtitle1" gutterBottom>
                            Berechtigungen zuweisen
                        </Typography>
                        
                        <Grid container spacing={2}>
                            {/* Verfügbare Berechtigungen */}
                            <Grid item xs={5}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Verfügbare Berechtigungen
                                </Typography>
                                <Paper variant="outlined">
                                    <List dense>
                                        {availablePermissions.map((permission) => (
                                            <ListItem
                                                key={permission.id || permission.name}
                                                button
                                                onClick={() => handleToggleAvailable(permission)}
                                            >
                                                <ListItemIcon>
                                                    <Checkbox
                                                        edge="start"
                                                        checked={checkedAvailable.indexOf(permission) !== -1}
                                                        tabIndex={-1}
                                                        disableRipple
                                                    />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={permission.name}
                                                    secondary={permission.description}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            </Grid>

                            {/* Pfeile für Bewegung */}
                            <Grid item xs={2} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleMoveToSelected}
                                    disabled={checkedAvailable.length === 0}
                                    sx={{ mb: 1 }}
                                >
                                    <ArrowForwardIcon />
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleMoveToAvailable}
                                    disabled={checkedSelected.length === 0}
                                >
                                    <ArrowBackIcon />
                                </Button>
                            </Grid>

                            {/* Ausgewählte Berechtigungen */}
                            <Grid item xs={5}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Zugewiesene Berechtigungen
                                </Typography>
                                <Paper variant="outlined">
                                    <List dense>
                                        {selectedPermissions.map((permission) => (
                                            <ListItem
                                                key={permission.id || permission.name}
                                                button
                                                onClick={() => handleToggleSelected(permission)}
                                            >
                                                <ListItemIcon>
                                                    <Checkbox
                                                        edge="start"
                                                        checked={checkedSelected.indexOf(permission) !== -1}
                                                        tabIndex={-1}
                                                        disableRipple
                                                    />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={permission.name}
                                                    secondary={permission.description}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setEditDialogOpen(false);
                        setCreateDialogOpen(false);
                        setSelectedRole(null);
                    }}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={createDialogOpen ? handleCreateSave : handleEditSave}
                        variant="contained"
                        color="primary"
                        disabled={!editData.name}
                    >
                        {createDialogOpen ? 'Erstellen' : 'Speichern'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Lösch-Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Rolle löschen</DialogTitle>
                <DialogContent>
                    <Typography>
                        Sind Sie sicher, dass Sie die Rolle "{selectedRole?.name}" löschen möchten?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                    >
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RoleList; 